import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputBase,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import LANGUAGES from '../../i18n/languages';
import { useLanguageSelection } from '../../contexts/LanguageSelectionContext';

type LanguageSelectorProps = {
  mobile?: boolean;
};

const BELGIAN_LANGUAGE_ID = 'be';

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ mobile = false }) => {
  const { i18n } = useTranslation();
  const { selectedFlag, setSelectedFlag } = useLanguageSelection();
  const [isBelgianDialogOpen, setBelgianDialogOpen] = useState(false);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const language = event.target.value;

    setSelectedFlag(language);

    if (language === BELGIAN_LANGUAGE_ID) {
      setBelgianDialogOpen(true);
      return;
    }

    i18n.changeLanguage(language);
  };

  const handleBelgianOptionClick = () => {
    setBelgianDialogOpen(true);
  };

  const handleBelgianLanguageChange = (language: 'fr' | 'nl') => {
    setSelectedFlag(BELGIAN_LANGUAGE_ID);
    i18n.changeLanguage(language);
    setSelectedFlag(BELGIAN_LANGUAGE_ID);
    setBelgianDialogOpen(false);
  };

  const selectedLanguage = LANGUAGES.find((language) => language.id === selectedFlag);
  const selectedFlagAlt = selectedLanguage?.name ?? 'Belgique / België';
  const flagWidth = mobile ? 20 : 35;

  return (
    <>
      <FormControl style={{ width: mobile ? 60 : 70 }}>
        <Select
          style={{ height: mobile ? undefined : 65 }}
          labelId="language-selector-label"
          id="language-selector"
          value={selectedFlag}
          label="Langue"
          onChange={handleChange}
          input={<InputBase />}
          renderValue={() => (
            <img
              src={`/images/flags/${selectedFlag}.svg`}
              alt={selectedFlagAlt}
              style={{ width: flagWidth, ...(mobile ? { marginBottom: -7 } : {}) }}
            />
          )}
        >
          {LANGUAGES.map((language) => (
            <MenuItem
              key={language.id}
              value={language.id}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
                onClick={language.id === BELGIAN_LANGUAGE_ID ? handleBelgianOptionClick : undefined}
              >
                <img
                  src={`/images/flags/${language.id}.svg`}
                  alt={language.name}
                  style={{ width: mobile ? 20 : 35 }}
                />
                <Typography
                  className="hover-color"
                  style={{ fontSize: mobile ? 11 : '14px' }}
                >
                  {language.name}
                </Typography>
              </div>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Dialog
        open={isBelgianDialogOpen}
        onClose={() => setBelgianDialogOpen(false)}
        aria-labelledby="belgian-language-dialog-title"
        fullWidth
        maxWidth="md"
        PaperProps={{
          style: {
            borderRadius: 0,
            padding: mobile ? '24px 12px' : '36px 24px',
          },
        }}
      >
        <DialogTitle
          id="belgian-language-dialog-title"
          style={{
            textAlign: 'center',
            fontSize: mobile ? 18 : 22,
            lineHeight: 1.35,
            padding: 0,
          }}
        >
          Veuillez choisir la langue pour accéder au site.
          <br />
          Kies de taal om toegang te krijgen tot de site.
        </DialogTitle>
        <DialogContent
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 8,
            flexWrap: 'wrap',
            padding: mobile ? '24px 0 0' : '32px 0 0',
          }}
        >
          <Button
            variant="contained"
            onClick={() => handleBelgianLanguageChange('fr')}
            style={{
              minHeight: mobile ? 58 : 74,
              flex: '1 1 220px',
              maxWidth: 320,
              fontSize: mobile ? 14 : 18,
              fontWeight: 700,
            }}
          >
            FRANÇAIS / FRANS
          </Button>
          <Button
            variant="contained"
            onClick={() => handleBelgianLanguageChange('nl')}
            style={{
              minHeight: mobile ? 58 : 74,
              flex: '1 1 220px',
              maxWidth: 320,
              fontSize: mobile ? 14 : 18,
              fontWeight: 700,
            }}
          >
            NÉERLANDAIS / DUTCH
          </Button>
        </DialogContent>
        <DialogActions style={{ justifyContent: 'center', padding: '16px 0 0' }}>
          <Button onClick={() => setBelgianDialogOpen(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LanguageSelector;
